import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Search,
  Clock,
  ArrowRight,
  Lock,
  Sparkles,
  BookOpen,
  Briefcase,
  ShieldCheck,
} from "lucide-react";
import useSeo from "../hooks/useSeo";
import { insights, caseStudies } from "../mock";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TAB_ALL = "all";
const TAB_INSIGHTS = "insights";
const TAB_CASES = "cases";

const readLocalUser = () => {
  try {
    const raw = localStorage.getItem("astoria_member_user");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const Library = () => {
  useSeo({
    title: "Members Library",
    description:
      "Full library of Astoria Solutions insights, briefings and case studies — unlocked for signed-in members.",
    path: "/library",
  });
  const navigate = useNavigate();
  const [user, setUser] = useState(readLocalUser);
  const [loading, setLoading] = useState(!user);
  const [tab, setTab] = useState(TAB_ALL);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("All");

  // Hydrate the member session — supports both custom JWT (localStorage)
  // and the Emergent Google session cookie flow.
  useEffect(() => {
    if (user) return;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setUser({
          username: data.name || data.email,
          email: data.email,
          picture: data.picture,
          auth_provider: data.auth_provider,
        });
      } catch {
        navigate("/member-login?redirect=%2Flibrary", { replace: true });
      } finally {
        setLoading(false);
      }
    })();
  }, [user, navigate]);

  const cases = useMemo(
    () =>
      Object.entries(caseStudies).flatMap(([category, items]) =>
        items.map((c) => ({
          ...c,
          category: category.charAt(0).toUpperCase() + category.slice(1),
          kind: "case",
          href: `/case-studies/${c.slug}`,
        }))
      ),
    []
  );

  const notes = useMemo(
    () =>
      insights.map((p) => ({
        ...p,
        kind: "insight",
        href: `/insights/${p.slug}`,
      })),
    []
  );

  const combined = useMemo(() => {
    if (tab === TAB_INSIGHTS) return notes;
    if (tab === TAB_CASES) return cases;
    return [...notes, ...cases];
  }, [tab, notes, cases]);

  const categories = useMemo(() => {
    const set = new Set(combined.map((i) => i.category));
    return ["All", ...set];
  }, [combined]);

  const filtered = combined.filter((i) => {
    const okC = cat === "All" || i.category === cat;
    const hay = `${i.title || ""} ${i.excerpt || i.quote || ""} ${i.client || ""}`.toLowerCase();
    const okQ = hay.includes(q.toLowerCase());
    return okC && okQ;
  });

  if (loading) {
    return (
      <section className="min-h-[70vh] flex items-center justify-center bg-neutral-50">
        <p className="text-sm text-neutral-500" data-testid="library-loading">
          Loading your library…
        </p>
      </section>
    );
  }
  if (!user) return null;

  const premiumCount =
    notes.filter((n) => n.premium).length + cases.filter((c) => c.premium).length;

  return (
    <>
      {/* Hero */}
      <section className="relative bg-black text-white overflow-hidden" data-testid="library-hero">
        <div className="absolute -top-24 -left-24 w-96 h-96 rounded-full bg-teal-500/20 blur-3xl" />
        <div className="absolute -bottom-32 -right-24 w-96 h-96 rounded-full bg-teal-400/10 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-6 py-16 md:py-20">
          <div className="flex items-center gap-2 text-teal-400 text-xs font-bold tracking-widest">
            <ShieldCheck className="w-4 h-4" /> MEMBERS-ONLY LIBRARY
          </div>
          <h1 className="mt-3 text-4xl md:text-6xl font-extrabold leading-tight">
            Welcome back, <span className="text-teal-400">{user.username || user.email}</span>
          </h1>
          <p className="mt-4 max-w-2xl text-neutral-300">
            Every insight, briefing and case study Astoria has published — unlocked and searchable
            in one place.
          </p>

          <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-3 max-w-3xl">
            <div className="bg-white/5 border border-white/10 rounded-2xl p-4">
              <BookOpen className="w-5 h-5 text-teal-400" />
              <div className="mt-2 text-2xl font-extrabold">{notes.length}</div>
              <div className="text-xs text-neutral-400">Insights</div>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-2xl p-4">
              <Briefcase className="w-5 h-5 text-teal-400" />
              <div className="mt-2 text-2xl font-extrabold">{cases.length}</div>
              <div className="text-xs text-neutral-400">Case studies</div>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-2xl p-4">
              <Sparkles className="w-5 h-5 text-teal-400" />
              <div className="mt-2 text-2xl font-extrabold">{premiumCount}</div>
              <div className="text-xs text-neutral-400">Premium unlocked</div>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-2xl p-4">
              <ShieldCheck className="w-5 h-5 text-teal-400" />
              <div className="mt-2 text-2xl font-extrabold">Live</div>
              <div className="text-xs text-neutral-400">Membership</div>
            </div>
          </div>
        </div>
      </section>

      {/* Filters */}
      <section className="bg-white border-b border-neutral-100 sticky top-16 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex gap-2" data-testid="library-tabs">
            {[
              { key: TAB_ALL, label: "All", count: notes.length + cases.length },
              { key: TAB_INSIGHTS, label: "Insights", count: notes.length },
              { key: TAB_CASES, label: "Case Studies", count: cases.length },
            ].map((t) => (
              <button
                key={t.key}
                data-testid={`library-tab-${t.key}`}
                onClick={() => {
                  setTab(t.key);
                  setCat("All");
                }}
                className={`px-4 py-2 rounded-full text-sm font-semibold border transition-colors ${
                  tab === t.key
                    ? "bg-black border-black text-white"
                    : "bg-white border-neutral-200 text-neutral-700 hover:border-teal-400 hover:text-teal-600"
                }`}
              >
                {t.label}
                <span
                  className={`ml-2 text-[10px] rounded-full px-1.5 py-0.5 ${
                    tab === t.key ? "bg-teal-500 text-white" : "bg-neutral-100 text-neutral-500"
                  }`}
                >
                  {t.count}
                </span>
              </button>
            ))}
          </div>

          <div className="flex flex-col sm:flex-row gap-3 md:items-center">
            <div className="flex gap-2 flex-wrap">
              {categories.map((c) => (
                <button
                  key={c}
                  data-testid={`library-cat-${c.toLowerCase()}`}
                  onClick={() => setCat(c)}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                    cat === c
                      ? "bg-teal-500 border-teal-500 text-white"
                      : "bg-white border-neutral-200 text-neutral-700 hover:border-teal-400 hover:text-teal-600"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
            <div className="relative w-full md:w-72">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
              <input
                data-testid="library-search-input"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search the library"
                className="w-full pl-9 pr-3 py-2 rounded-full border border-neutral-200 focus:outline-none focus:ring-2 focus:ring-teal-400 text-sm"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Grid */}
      <section className="bg-neutral-50 py-12">
        <div className="max-w-7xl mx-auto px-6">
          {filtered.length === 0 ? (
            <p className="text-center text-neutral-500 py-16" data-testid="library-empty">
              Nothing matches your search yet — try a different term or category.
            </p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="library-grid">
              {filtered.map((item) => (
                <Link
                  key={`${item.kind}-${item.slug}`}
                  to={item.href}
                  data-testid={`library-card-${item.slug}`}
                  className="group bg-white rounded-2xl overflow-hidden border border-neutral-100 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 flex flex-col"
                >
                  <div className="relative h-52 overflow-hidden">
                    <img
                      src={item.image}
                      alt={item.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <span className="absolute top-3 left-3 bg-teal-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                      {item.category}
                    </span>
                    <span
                      className={`absolute top-3 right-3 inline-flex items-center gap-1 text-[10px] font-bold tracking-widest px-2.5 py-1 rounded-full ${
                        item.kind === "case"
                          ? "bg-black text-teal-400"
                          : "bg-white/90 text-neutral-900 border border-neutral-100"
                      }`}
                    >
                      {item.kind === "case" ? "CASE STUDY" : "INSIGHT"}
                    </span>
                    {item.premium && (
                      <span className="absolute bottom-3 left-3 inline-flex items-center gap-1 bg-amber-500 text-black text-[10px] font-bold tracking-widest px-2.5 py-1 rounded-full">
                        <Lock className="w-3 h-3" /> UNLOCKED
                      </span>
                    )}
                  </div>
                  <div className="p-6 flex-1 flex flex-col">
                    {item.kind === "insight" ? (
                      <>
                        <div className="flex items-center gap-3 text-xs text-neutral-500">
                          <span>{item.date}</span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" /> {item.readTime}
                          </span>
                        </div>
                        <h3 className="mt-2 text-lg font-bold text-neutral-900 leading-snug group-hover:text-teal-600 transition-colors">
                          {item.title}
                        </h3>
                        <p className="mt-2 text-sm text-neutral-600 leading-relaxed">{item.excerpt}</p>
                        <div className="mt-auto pt-4 flex items-center justify-between">
                          <span className="text-xs text-neutral-500">By {item.author}</span>
                          <span className="inline-flex items-center gap-1 text-teal-600 text-sm font-semibold group-hover:gap-2 transition-all">
                            Read <ArrowRight className="w-4 h-4" />
                          </span>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="text-xs font-bold text-teal-600 tracking-widest">
                          {item.client}
                        </div>
                        <h3 className="mt-2 text-lg font-bold text-neutral-900 leading-snug group-hover:text-teal-600 transition-colors">
                          {item.title}
                        </h3>
                        <div className="mt-3 flex items-baseline gap-2">
                          <span className="text-2xl font-extrabold text-neutral-900">
                            {item.metric}
                          </span>
                          <span className="text-xs text-neutral-500">{item.metricLabel}</span>
                        </div>
                        <div className="mt-auto pt-4 flex items-center justify-between">
                          <span className="text-xs text-neutral-500">{item.industry}</span>
                          <span className="inline-flex items-center gap-1 text-teal-600 text-sm font-semibold group-hover:gap-2 transition-all">
                            Open <ArrowRight className="w-4 h-4" />
                          </span>
                        </div>
                      </>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </>
  );
};

export default Library;
